import * as CookieConsent from 'vanilla-cookieconsent';

// 1. Typy
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (command: string, ...args: unknown[]) => void;
    __loadGA?: () => void;
    __gaLoaded?: boolean;
  }
}

// Ewidencja zgód cookie do Google Sheets (wymagane RODO Art. 7 ust. 1 — dowód zgody)
const POLICY_VERSION = '2026-01-05';

const logConsent = (cookie: { categories: string[]; consentId: string }) => {
  try {
    const URL = 'https://script.google.com/macros/s/AKfycbyrnJUqxRQzZWBddD9bT6NR6nXchJQwsEooJUmt4Vjmk8_gSnY6LufQyn8z3C9uZQ37/exec';
    fetch(URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        site: window.location.hostname,
        consentId: cookie.consentId,
        accepted: cookie.categories,
        userAgent: navigator.userAgent,
        policyVersion: POLICY_VERSION
      })
    });
  } catch (_) {}
};

// 2. Aktualizacja Google Consent Mode + dynamiczne ładowanie GA
const updateGtagConsent = (categories: string[]) => {
  if (typeof window === 'undefined') return;
  const hasAnalytics = categories.includes('analytics');

  // Załaduj skrypt GA dopiero po uzyskaniu zgody na analytics
  if (hasAnalytics) {
    if (typeof window.__loadGA === 'function') window.__loadGA();
  }

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: hasAnalytics ? 'granted' : 'denied',
      personalization_storage: 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
    });
  }
};

export const initCookieConsent = () => {
  if (typeof window === 'undefined') return;

  // Setup dataLayer
  if (!window.dataLayer) window.dataLayer = [];
  if (!window.gtag) {
    window.gtag = function (...args: unknown[]) {
      window.dataLayer.push(args);
    };
  }

  CookieConsent.run({
    revision: 1,
    guiOptions: {
      consentModal: {
        layout: 'box',
        position: 'bottom left',
        equalWeightButtons: true,
        flipButtons: false
      },
      preferencesModal: {
        layout: 'box',
        position: 'right',
        equalWeightButtons: true,
        flipButtons: false
      }
    },

    // 3. Kategorie
    categories: {
      necessary: {
        readOnly: true,
        enabled: true
      },
      analytics: {
        enabled: false,
        autoClear: {
          cookies: [
            { name: /^_ga/ },
            { name: '_gid' }
          ]
        }
      }
    },

    // 4. Języki
    language: {
      default: 'pl',
      autoDetect: 'browser',
      translations: {
        pl: {
          consentModal: {
            title: 'Szanujemy Twoją prywatność',
            description: 'Używamy plików cookie głównie w celach analitycznych, aby zrozumieć, jak korzystasz z naszej strony i móc ją ulepszać. Nie wyświetlamy reklam ani nie profilujemy Cię marketingowo. Więcej informacji znajdziesz w naszej <a href="/polityka-prywatnosci">Polityce Prywatności</a>.',
            acceptAllBtn: 'Zaakceptuj wszystko',
            acceptNecessaryBtn: 'Tylko niezbędne',
            showPreferencesBtn: 'Zarządzaj zgodami',
            footer: '<a href="/polityka-prywatnosci">Polityka Prywatności</a>\n<a href="/terms">Regulamin</a>'
          },
          preferencesModal: {
            title: 'Preferencje plików cookie',
            acceptAllBtn: 'Zaakceptuj wszystko',
            acceptNecessaryBtn: 'Tylko niezbędne',
            savePreferencesBtn: 'Zapisz ustawienia',
            closeIconLabel: 'Zamknij',
            serviceCounterLabel: 'Usługi',
            sections: [
              {
                title: 'Wykorzystanie plików cookie',
                description: 'Używamy plików cookie, aby zapewnić podstawowe funkcje witryny oraz poprawić jakość naszych usług. Poniżej możesz wybrać, na które kategorie plików cookie wyrażasz zgodę.'
              },
              {
                title: 'Niezbędne pliki cookie <span class="pm__badge">Wymagane</span>',
                description: 'Te pliki cookie są niezbędne do działania strony (np. logowanie, bezpieczeństwo). Nie możesz ich wyłączyć.',
                linkedCategory: 'necessary'
              },
              {
                title: 'Analityczne pliki cookie',
                description: 'Te pliki cookie pozwalają nam zliczać wizyty i źródła ruchu, dzięki czemu możemy mierzyć i poprawiać wydajność naszej witryny. Wszystkie informacje gromadzone przez te pliki są agregowane i anonimowe.',
                linkedCategory: 'analytics',
                cookieTable: {
                  headers: {
                    name: 'Nazwa',
                    domain: 'Serwis',
                    desc: 'Opis',
                    exp: 'Wygasa'
                  },
                  body: [
                    {
                      name: '_ga',
                      domain: 'Google Analytics',
                      desc: 'Służy do rozróżniania unikalnych użytkowników.',
                      exp: '2 lata'
                    },
                    {
                      name: '_ga_*',
                      domain: 'Google Analytics',
                      desc: 'Przechowuje stan sesji.',
                      exp: '2 lata'
                    }
                  ]
                }
              }
            ]
          }
        }
      }
    },

    // 5. Callbacki
    onFirstConsent: ({ cookie }) => {
      updateGtagConsent(cookie.categories);
      logConsent(cookie);
    },

    onConsent: ({ cookie }) => {
      updateGtagConsent(cookie.categories);
    },

    onChange: ({ cookie }) => {
      updateGtagConsent(cookie.categories);
      logConsent(cookie);
    }
  });
};

export const showPreferences = () => CookieConsent.showPreferences();
export const resetConsent = () => CookieConsent.reset(true);
export const showBanner = () => CookieConsent.show(true);
